import { Controller, useFormContext, useWatch } from 'react-hook-form';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { FormFieldTimePicker } from '@/components/FormFieldTimePicker';
import FormFieldPhone from '@/components/FormFieldPhone';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ADDRESS_LINE_VALIDATION, STATE_VALIDATION } from '@/utils/constant';
import {
  MARITAL_STATUS_OPTIONS,
  type HiAttemptFormValues,
} from '../hiAttempt.schema';

const DISABLED_STYLE = 'disabled:opacity-100 text-black';

type Props = {
  workOrder: {
    createdAt?: string | Date;
    server?: { firstName: string; lastName: string } | null;
  };
  readOnly?: boolean;
  /** When true, date/time are collected elsewhere (e.g. PS Service Information step). */
  hideDateTime?: boolean;
};

type YesNoField = 'ownerOccupied' | 'vacant' | 'bothOwnersResideTogether' | 'defendantInArmedForces';

const YesNoRadio = ({
  name,
  label,
  readOnly,
}: {
  name: YesNoField;
  label: string;
  readOnly?: boolean;
}) => {
  const { control } = useFormContext<HiAttemptFormValues>();
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <RadioGroup
            value={field.value ?? ''}
            onValueChange={(v) => field.onChange(v as 'YES' | 'NO')}
            disabled={readOnly}
            className="flex flex-row gap-6 h-10 items-center"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="YES" id={`${name}-yes`} />
              <Label htmlFor={`${name}-yes`} className="cursor-pointer font-normal">
                Yes
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="NO" id={`${name}-no`} />
              <Label htmlFor={`${name}-no`} className="cursor-pointer font-normal">
                No
              </Label>
            </div>
          </RadioGroup>
        )}
      />
    </div>
  );
};

export const HiAttemptStep1OwnerInfo = ({ workOrder, readOnly, hideDateTime }: Props) => {
  const { control } = useFormContext<HiAttemptFormValues>();
  const maritalStatus = useWatch({ control, name: 'maritalStatus' });

  const woCreatedAt = workOrder.createdAt ? new Date(workOrder.createdAt) : undefined;

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      {!hideDateTime && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
          <FormFieldCalendar<HiAttemptFormValues>
            control={control}
            name="dateOfAttempt"
            label="Date of Attempt"
            placeholder="Select date"
            required
            disabled={readOnly}
            disabledStyle={readOnly ? DISABLED_STYLE : undefined}
            disabledAt={
              woCreatedAt
                ? (date) => {
                    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const woDateOnly = new Date(
                      woCreatedAt.getFullYear(),
                      woCreatedAt.getMonth(),
                      woCreatedAt.getDate()
                    );
                    return dateOnly < woDateOnly;
                  }
                : undefined
            }
          />
          <FormFieldTimePicker
            name="timeOfAttempt"
            label="Time of Attempt"
            required
            use12HourFormat
            timeFields={['H', 'M']}
            returnFormattedValue
            disabled={readOnly}
            disabledStyle={DISABLED_STYLE}
          />
        </div>
      )}

      <div className="text-sm font-semibold text-primary mt-2">Owner Present Address</div>

      <div className="grid grid-cols-1 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="ownerAddressLine1"
          label="Address Line 1"
          placeholder="Address Line 1"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          {...ADDRESS_LINE_VALIDATION}
        />
        <FormFieldComponent
          control={control}
          name="ownerAddressLine2"
          label="Address Line 2"
          placeholder="Address Line 2"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          {...ADDRESS_LINE_VALIDATION}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="ownerCity"
          label="City"
          placeholder="City"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
        />
        <FormFieldComponent
          control={control}
          name="ownerState"
          label="State"
          placeholder="e.g. NY"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          {...STATE_VALIDATION}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="ownerZipCode"
          label="Zip Code"
          placeholder="Zip Code"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
        />
        <FormFieldPhone<HiAttemptFormValues> name="ownerPhone" label="Owner Telephone" disabled={readOnly} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSelect
          name="maritalStatus"
          options={MARITAL_STATUS_OPTIONS}
          placeholder="Select Marital Status"
          label="Marital Status"
          disabled={readOnly}
        />
        {maritalStatus === 'MARRIED' && (
          <FormFieldComponent
            control={control}
            name="spouseName"
            label="Spouse Name"
            placeholder="Spouse Name"
            required
            disabled={readOnly}
            disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <YesNoRadio name="ownerOccupied" label="Owner Occupied" readOnly={readOnly} />
        <YesNoRadio name="vacant" label="Vacant" readOnly={readOnly} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <YesNoRadio name="bothOwnersResideTogether" label="Owners Reside Together" readOnly={readOnly} />
        <YesNoRadio name="defendantInArmedForces" label="Defendant in Armed Forces" readOnly={readOnly} />
      </div>

      <div className="text-sm font-semibold text-primary mt-2">Co-Owner Details</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="coOwnerName"
          label="Co-Owner Name"
          placeholder="Co-Owner Name"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="coOwnerAddressLine1"
          label="Co-Owner Address Line 1"
          placeholder="Address Line 1"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          {...ADDRESS_LINE_VALIDATION}
        />
        <FormFieldComponent
          control={control}
          name="coOwnerAddressLine2"
          label="Co-Owner Address Line 2"
          placeholder="Address Line 2"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          {...ADDRESS_LINE_VALIDATION}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="coOwnerCity"
          label="Co-Owner City"
          placeholder="City"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
        />
        <FormFieldComponent
          control={control}
          name="coOwnerState"
          label="Co-Owner State"
          placeholder="e.g. NY"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          {...STATE_VALIDATION}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="coOwnerZipCode"
          label="Co-Owner Zip Code"
          placeholder="Zip Code"
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
        />
      </div>
    </div>
  );
};
